import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import { authAPI } from '../../utils/authAPI';
import { PLATFORM_NAME } from '../../utils/platform';

// Perfil en SOLO LECTURA. Upstream permitía editar nombre y correo y cambiar la contraseña desde
// aquí; en Vassago la identidad la administra Authentik, así que este panel solo muestra lo que
// llegó en el token y manda al usuario a cambiarlo donde de verdad vive.
//
// De paso desaparece el endpoint que había detrás (PUT /api/auth/me), que verificaba la
// contraseña actual con bcrypt sin ningún rate limit y permitía adivinarla por fuerza bruta.
const IDENTITY_URL = process.env.REACT_APP_IDENTITY_URL || 'https://id.mexicosadecv.com.mx';

const ProfileTab = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    authAPI.getCurrentUser()
      .then(setUser)
      .catch((err) => {
        console.error('Error fetching current user:', err);
        setError(err.message);
      });
  }, []);

  if (error) {
    return <div className="text-sm text-red-600 dark:text-red-400">{error}</div>;
  }

  if (!user) {
    return <div className="text-sm text-slate-500 dark:text-slate-400">{t('common.loading')}</div>;
  }

  const rows = [
    { label: t('settings.profile.username'), value: user.username },
    { label: t('settings.profile.email'), value: user.email || '—' },
    { label: t('settings.profile.displayName'), value: user.display_name || '—' },
  ];

  return (
    <div className="max-w-xl space-y-6">
      <div className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
        <ShieldCheck className="w-5 h-5 mt-0.5 text-accent-primary flex-shrink-0" aria-hidden="true" />
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {t('settings.profile.managedExternally', { platform: PLATFORM_NAME })}
        </p>
      </div>

      <dl className="divide-y divide-slate-200 dark:divide-slate-700">
        {rows.map((row) => (
          <div key={row.label} className="py-3 flex justify-between gap-4">
            <dt className="text-sm text-slate-500 dark:text-slate-400">{row.label}</dt>
            <dd className="text-sm font-medium text-slate-900 dark:text-slate-100 text-right break-all">
              {row.value}
            </dd>
          </div>
        ))}
        {user.isAdmin && (
          <div className="py-3 flex justify-between gap-4">
            <dt className="text-sm text-slate-500 dark:text-slate-400">{t('settings.profile.role')}</dt>
            <dd className="text-sm font-medium text-accent-primary">{t('settings.profile.roleAdmin')}</dd>
          </div>
        )}
      </dl>

      <a
        href={IDENTITY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-sm font-medium text-accent-primary hover:underline"
      >
        {t('settings.profile.manageIdentity')}
        <ExternalLink className="w-4 h-4" aria-hidden="true" />
      </a>
    </div>
  );
};

export default ProfileTab;
